import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/utils';

export type ConfirmModalType = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  type?: ConfirmModalType;
  confirmLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  className?: string;
}

/**
 * Standardized confirmation modal
 * Provides consistent confirmation dialogs with different types
 */
export function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'danger',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  isLoading = false,
  className,
}: ConfirmModalProps) {
  if (!isOpen) return null;

  const getIcon = () => {
    const iconClass = 'w-6 h-6';
    switch (type) {
      case 'danger':
        return <XCircle className={iconClass} style={{ color: 'var(--color-error)' }} />;
      case 'warning':
        return <AlertTriangle className={iconClass} style={{ color: 'var(--color-warning)' }} />;
      case 'info':
        return <Info className={iconClass} style={{ color: 'var(--color-accent)' }} />;
      case 'success':
        return <CheckCircle className={iconClass} style={{ color: 'var(--color-success)' }} />;
    }
  };

  const getConfirmVariant = (): 'primary' | 'destructive' => {
    return type === 'danger' ? 'destructive' : 'primary';
  };

  const handleConfirm = async () => {
    await onConfirm();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
    >
      <div
        className={cn(
          'rounded-2xl shadow-2xl max-w-md w-full border relative',
          className
        )}
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">{getIcon()}</div>
            <div className="flex-1">
              <h3 id="confirm-modal-title" className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                {title}
              </h3>
              <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                {message}
              </p>
              <div className="flex justify-end space-x-3">
                <Button variant="outline" onClick={onClose} disabled={isLoading}>
                  {cancelLabel}
                </Button>
                <Button
                  variant={getConfirmVariant()}
                  onClick={handleConfirm}
                  isLoading={isLoading}
                >
                  {confirmLabel}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

